import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useCallback } from "react";
import { useGetPatientIdentificationQuery } from "src/api/services/api";
import { useUpdatePatientIdVerificationMutation } from "src/api/services/idVerificationApi";
import { Task } from "src/api/types";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import FileView from "src/shared/FileView";
import TaskWrapper from "src/wrappers/TaskWrapper";

interface Props {
  profileId: string;
  task: Task;
  onSuccess: () => void;
}

const IdVerificationTask = ({ task, profileId, onSuccess }: Props) => {
  const reviewerId = useClinician((c) => c.id);
  const [updateIdVerification, { isLoading }] =
    useUpdatePatientIdVerificationMutation();
  const { data: files } = useGetPatientIdentificationQuery({
    profileId,
    folderName: "identification",
  });

  const handleApprove = useCallback(() => {
    const reviewedAt = new Date().toISOString();
    updateIdVerification({
      id: task.resource_id,
      status: "verified",
      verification_type: "manual",
      verified_at: reviewedAt,
      reviewer_id: reviewerId,
    })
      .unwrap()
      .then(onSuccess);
  }, [onSuccess, reviewerId, task.resource_id, updateIdVerification]);

  const handleDeny = useCallback(() => {
    const reviewedAt = new Date().toISOString();
    updateIdVerification({
      id: task.resource_id,
      status: "denied",
      verification_type: "manual",
      rejected_at: reviewedAt,
      reviewer_id: reviewerId,
    })
      .unwrap()
      .then(onSuccess);
  }, [onSuccess, reviewerId, task.resource_id, updateIdVerification]);

  return (
    <TaskWrapper task={task}>
      <Stack gap={1} direction="row" width="100%" justifyContent="center">
        {files?.map((file) => (
          <FileView
            file={file}
            key={file.url}
            label={
              file.name.includes("front-of-card")
                ? "Patient ID"
                : "Patient Selfie"
            }
          />
        ))}
      </Stack>

      <Stack direction="row" gap={1} justifyContent="center">
        <Button onClick={handleApprove} loading={isLoading}>
          Approve
        </Button>
        <Button onClick={handleDeny} variant="secondary" loading={isLoading}>
          Deny
        </Button>
      </Stack>
    </TaskWrapper>
  );
};

export default IdVerificationTask;
